"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import AutoCreditorPostModal from "./AutoCreditorPostModal";
import type { SuggestionView } from "@/types/suggestions";

type Props = {
  suggestions: SuggestionView[];
  companySlug: string;
};

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
const TARGET_IMAGE_BYTES = 6 * 1024 * 1024;

export default function InvoicesInboxScreen({ suggestions, companySlug }: Props) {
  const router = useRouter();
  const [open, setOpen] = React.useState(false);
  const [note, setNote] = React.useState<string | null>(null);
  const [applyingId, setApplyingId] = React.useState<string | null>(null);
  const [scanTitle, setScanTitle] = React.useState("");
  const [scanFile, setScanFile] = React.useState<File | null>(null);
  const [scanning, setScanning] = React.useState(false);
  const [scanHint, setScanHint] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const cameraInputRef = React.useRef<HTMLInputElement | null>(null);

  function humanSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function compressImageIfUseful(file: File): Promise<File> {
    if (!file.type.startsWith("image/")) return file;
    if (file.type === "image/gif" || file.type === "image/svg+xml") return file;
    if (file.size <= TARGET_IMAGE_BYTES) return file;

    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const src = URL.createObjectURL(file);
      const i = new Image();
      i.onload = () => {
        URL.revokeObjectURL(src);
        resolve(i);
      };
      i.onerror = () => {
        URL.revokeObjectURL(src);
        reject(new Error("Bild konnte nicht gelesen werden"));
      };
      i.src = src;
    });

    const maxEdges = [2200, 1800, 1500, 1280];
    const qualities = [0.84, 0.76, 0.68, 0.58];
    let bestBlob: Blob | null = null;

    for (const maxEdge of maxEdges) {
      const ratio = Math.min(1, maxEdge / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * ratio));
      const h = Math.max(1, Math.round(img.height * ratio));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      ctx.drawImage(img, 0, 0, w, h);

      for (const quality of qualities) {
        const blob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob(resolve, "image/jpeg", quality);
        });
        if (!blob) continue;
        if (!bestBlob || blob.size < bestBlob.size) bestBlob = blob;
        if (blob.size <= TARGET_IMAGE_BYTES) {
          bestBlob = blob;
          break;
        }
      }
      if (bestBlob && bestBlob.size <= TARGET_IMAGE_BYTES) break;
    }

    if (!bestBlob) return file;
    if (bestBlob.size >= file.size * 0.95) return file;

    const outName = file.name.replace(/\.[a-zA-Z0-9]+$/, "") + ".jpg";
    return new File([bestBlob], outName, {
      type: "image/jpeg",
      lastModified: Date.now()
    });
  }

  async function onApplySuggestion(suggestionId: string) {
    try {
      setApplyingId(suggestionId);
      const res = await fetch("/api/suggestions/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ suggestionId, companySlug })
      });
      const data = (await res.json()) as { ok?: boolean; error?: string; entryId?: string };
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Vorschlag konnte nicht angewendet werden");
      }
      setNote(`✅ Vorschlag angewendet. Provisorische Buchung ${data.entryId} wurde erstellt.`);
      router.refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unbekannter Fehler";
      setNote(`❌ ${message}`);
    } finally {
      setApplyingId(null);
    }
  }

  function statusClass(status: SuggestionView["status"]): string {
    if (status === "APPLIED") return "pill pill-final";
    if (status === "REJECTED") return "pill";
    return "pill pill-provisional";
  }

  function governancePillClass(decision: SuggestionView["governanceDecision"]): string {
    if (decision === "AUTO_ALLOWED") return "pill pill-ok";
    if (decision === "BLOCKED") return "pill";
    return "pill pill-review";
  }

  async function uploadAndParseDocument(file: File) {
    setScanning(true);
    setNote(null);
    setScanHint(null);
    try {
      const prepared = await compressImageIfUseful(file);
      const fd = new FormData();
      fd.append("file", prepared);
      fd.append("companySlug", companySlug);
      if (scanTitle.trim()) {
        fd.append("documentTitle", scanTitle.trim());
      }
      const uploadRes = await fetch("/api/documents/upload", {
        method: "POST",
        body: fd
      });
      const uploadData = (await uploadRes.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        documentNo?: string;
      };
      if (!uploadRes.ok || !uploadData.documentNo) {
        throw new Error(uploadData.error || "Upload fehlgeschlagen");
      }

      const parseRes = await fetch("/api/jobs/document-parse", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companySlug, documentNo: uploadData.documentNo })
      });
      const parseData = (await parseRes.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!parseRes.ok || !parseData.ok) {
        throw new Error(parseData.error || "Parse-Job konnte nicht gestartet werden");
      }

      const compressedInfo =
        prepared !== file
          ? ` Bild komprimiert (${humanSize(file.size)} → ${humanSize(prepared.size)}).`
          : "";
      setNote(`✅ Beleg ${uploadData.documentNo} hochgeladen. Scan/Parse läuft im Hintergrund.${compressedInfo}`);
      setScanFile(null);
      setScanTitle("");
      setScanHint(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      router.refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload/Scan fehlgeschlagen";
      setNote(`❌ ${message}`);
    } finally {
      setScanning(false);
    }
  }

  function onFilePicked(file: File | null) {
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setScanFile(null);
      setScanHint(null);
      setNote(
        `❌ Datei ist zu groß (${humanSize(file.size)}). Maximal erlaubt sind ${humanSize(MAX_UPLOAD_BYTES)}.`
      );
      return;
    }
    setScanFile(file);
    if (file.type === "application/pdf") {
      setScanHint(`PDF gewählt (${humanSize(file.size)}). Sehr gut für mehrseitige Rechnungen.`);
      return;
    }
    if (file.type.startsWith("image/")) {
      if (file.size > TARGET_IMAGE_BYTES) {
        setScanHint(
          `Großes Foto (${humanSize(file.size)}). Vor Upload wird automatisch auf ca. ${humanSize(TARGET_IMAGE_BYTES)} optimiert. Für mehrseitige Belege bitte PDF verwenden.`
        );
      } else {
        setScanHint(`Bild gewählt (${humanSize(file.size)}). Für Einzelseiten ideal.`);
      }
      return;
    }
    setScanHint(`Datei gewählt (${humanSize(file.size)}).`);
  }

  async function onStartScanFlow() {
    if (!scanFile) {
      setNote("❌ Bitte zuerst eine Datei auswählen oder mit Kamera scannen.");
      return;
    }
    if (scanFile.size > MAX_UPLOAD_BYTES) {
      setNote(
        `❌ Datei ist zu groß (${humanSize(scanFile.size)}). Maximal erlaubt sind ${humanSize(MAX_UPLOAD_BYTES)}.`
      );
      return;
    }
    await uploadAndParseDocument(scanFile);
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Rechnungen · Inbox</h1>
          <p>Vorschläge prüfen und anwenden. Jeder angewendete Vorschlag erstellt eine provisorische Buchung.</p>
        </div>
        <div className="page-actions">
          <button className="primary-btn" type="button" onClick={() => setOpen(true)}>
            Auto-Buchen
          </button>
        </div>
      </div>

      {note && (
        <div className="table-wrap" style={{ padding: 14, marginBottom: 12 }}>
          {note}
        </div>
      )}

      <div className="table-wrap" style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>Beleg scannen / hochladen (iPhone, Samsung, PC)</div>
        <div style={{ color: "var(--muted)", fontSize: 12, marginBottom: 10 }}>
          Kamera-Button öffnet direkt die Handy-Kamera (wenn vom Browser unterstützt). Für Google Drive/Google Fotos: Datei zuerst lokal öffnen/exportieren und hier hochladen.
        </div>
        <div style={{ display: "grid", gap: 8, gridTemplateColumns: "minmax(220px, 1fr) minmax(220px, 1fr) minmax(220px, 1fr)" }}>
          <input
            className="input"
            placeholder="Titel (optional)"
            value={scanTitle}
            onChange={(e) => setScanTitle(e.target.value)}
            disabled={scanning}
          />
          <button
            className="secondary-btn"
            type="button"
            disabled={scanning}
            onClick={() => fileInputRef.current?.click()}
          >
            Aus Galerie wählen
          </button>
          <input
            ref={fileInputRef}
            style={{ display: "none" }}
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => onFilePicked(e.target.files?.[0] || null)}
            disabled={scanning}
          />
          <button
            className="secondary-btn"
            type="button"
            disabled={scanning}
            onClick={() => cameraInputRef.current?.click()}
          >
            Mit Handy-Kamera scannen
          </button>
          <input
            ref={cameraInputRef}
            style={{ display: "none" }}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => onFilePicked(e.target.files?.[0] || null)}
            disabled={scanning}
            title="Kamera öffnen"
          />
        </div>
        <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button className="primary-btn" type="button" onClick={onStartScanFlow} disabled={scanning || !scanFile}>
            {scanning ? "Upload/Scan läuft..." : "Upload + Scan starten"}
          </button>
          <span style={{ color: "var(--muted)", fontSize: 12 }}>
            {scanFile ? `Ausgewählt: ${scanFile.name}` : "Keine Datei gewählt"}
          </span>
        </div>
        {scanHint && (
          <div style={{ marginTop: 6, color: "var(--muted)", fontSize: 12 }}>
            {scanHint}
          </div>
        )}
        <div style={{ marginTop: 6, color: "var(--muted)", fontSize: 12 }}>
          Mehrseitig: Je nach Android-/iPhone-Browser wird die Dokument-Kamera mit Mehrseitenmodus angeboten. Falls nicht, Seiten einzeln scannen und nacheinander hochladen.
        </div>
      </div>

      <div className="table-wrap" style={{ padding: 14 }}>
        {suggestions.length === 0 ? (
          <p style={{ margin: 0 }}>Keine Vorschläge vorhanden. Lade zuerst einen Beleg hoch und starte den Parse-Job.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Beleg</th>
                <th>Lieferant</th>
                <th>Rechnung</th>
                <th>Betrag</th>
                <th>MWST-Hinweis</th>
                <th>Confidence</th>
                <th>KI-Governance</th>
                <th>Status</th>
                <th>Aktion</th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map((s) => (
                <tr key={s.id}>
                  <td>{s.documentNo}</td>
                  <td>{s.vendor || "-"}</td>
                  <td>{s.invoiceNo || "-"}</td>
                  <td>
                    {s.amount != null ? `${s.amount.toFixed(2)} ${s.currency || ""}`.trim() : "-"}
                  </td>
                  <td>
                    {s.suggestedVat
                      ? [
                          s.suggestedVat.vatCode || null,
                          s.suggestedVat.vatRatePercent != null ? `${s.suggestedVat.vatRatePercent}%` : null,
                          s.suggestedVat.amountType || null
                        ]
                          .filter(Boolean)
                          .join(" · ") || "-"
                      : "-"}
                  </td>
                  <td>{s.confidence != null ? `${Math.round(s.confidence * 100)}%` : "-"}</td>
                  <td title={s.governanceReasons?.join(" | ") || ""}>
                    <span className={governancePillClass(s.governanceDecision)}>{s.governanceDecision}</span>
                  </td>
                  <td>
                    <span className={statusClass(s.status)}>{s.status}</span>
                  </td>
                  <td>
                    <button
                      className="secondary-btn"
                      type="button"
                      disabled={s.status !== "SUGGESTED" || applyingId === s.id || s.governanceDecision === "BLOCKED"}
                      onClick={() => onApplySuggestion(s.id)}
                    >
                      {applyingId === s.id ? "Wird angewendet..." : "Vorschlag anwenden"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AutoCreditorPostModal
        open={open}
        onClose={() => setOpen(false)}
        companySlug={companySlug}
        onCreated={() => setNote("✅ Provisorische Buchung erstellt. Schau im Journal nach (Finance → Journal).")}
      />
    </div>
  );
}
